import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronDown, Loader2, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";

const COLOR_HEX = {
  ash: "#B2B4B8", black: "#181614", brown: "#5C4033", burgundy: "#6E1E2B",
  charcoal: "#36454F", cobalt: "#0047AB", cream: "#FFFDD0", "forest green": "#228B22",
  gold: "#D4AF37", grey: "#808080", ivory: "#FFFFF0", khaki: "#C3B091",
  lavender: "#B57EDC", mint: "#9ADBC6", navy: "#1B2A4A", olive: "#5A5B3A",
  orange: "#E07B39", pink: "#F4A7B9", purple: "#7D3C98", red: "#C0392B",
  "royal blue": "#2451E6", sand: "#C2B280", "sky blue": "#87CEEB", teal: "#008080",
  white: "#F5F0EB", yellow: "#F4D03F", blue: "#2B4BD8", green: "#3E6B48",
};

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [variants, setVariants] = useState({});
  const [variantLoading, setVariantLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const { addItem, setDrawerOpen } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setSize(null);
    setColor(null);
    setVariants({});
    setActiveImage(0);
    api.get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [id]);

  const selectColor = async (c) => {
    setColor(c);
    setColorOpen(false);
    if (!product || variants[c] || variantLoading) return;
    setVariantLoading(true);
    try {
      await api.post(`/products/${product.id}/variant`, { color: c });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/variant-image/${c}`;
      let ready = false;
      for (let i = 0; i < 45 && !ready; i++) {
        const res = await api.get(`/products/${product.id}/variant-image/${c}`, {
          validateStatus: () => true,
          responseType: "blob",
        });
        if (res.status === 200) ready = true;
        else if (res.status === 404) throw new Error("generation failed");
        else await new Promise((r) => setTimeout(r, 2500));
      }
      if (!ready) throw new Error("timeout");
      setVariants((prev) => ({ ...prev, [c]: url }));
    } catch {
      toast.error("Color preview unavailable for that one — ordering still works");
    } finally {
      setVariantLoading(false);
    }
  };

  if (notFound)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-32 text-center" data-testid="product-not-found">
        <p className="font-display text-2xl font-bold uppercase mb-4">Piece not found</p>
        <Link to="/shop" className="text-[#E05A36] font-mono text-xs uppercase tracking-widest">← Back to store</Link>
      </div>
    );

  if (!product)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-32 text-center text-[#7A7067] font-mono text-xs uppercase tracking-widest" data-testid="product-loading">
        Loading...
      </div>
    );

  const images = [product.image, product.image_alt].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#7A7067] hover:text-[#E05A36] transition-colors mb-10" data-testid="product-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to store
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="relative bg-[#171412] border border-[#2C2623] overflow-hidden">
            <motion.img
              key={variants[color] || activeImage}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src={variants[color] || images[activeImage]}
              alt={product.name}
              className="w-full aspect-[4/5] object-cover"
              data-testid="product-main-image"
            />
            {variantLoading && (
              <div className="absolute inset-0 bg-[#0C0A09]/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-10" data-testid="variant-loading">
                <Loader2 className="w-8 h-8 text-[#E05A36] animate-spin" />
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#B8AEA5]">
                  Dyeing it {color}... first preview takes a few seconds
                </p>
              </div>
            )}
            {product.tag && (
              <span className="absolute top-4 left-4 px-3 py-1 text-[10px] font-mono font-bold uppercase bg-[#E05A36]/90 text-[#0C0A09]">
                {product.tag}
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`w-20 aspect-[4/5] overflow-hidden border transition-colors ${activeImage === i ? "border-[#E05A36]" : "border-[#2C2623]"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#E05A36] mb-3">{product.category}</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight uppercase leading-[1.05] mb-4" data-testid="product-name">
            {product.name}
          </h1>
          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-mono text-2xl text-[#E29D47]" data-testid="product-price">${product.price.toFixed(2)}</span>
            {product.original_price > product.price && (
              <span className="font-mono text-sm text-[#7A7067] line-through">${product.original_price.toFixed(2)}</span>
            )}
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${product.in_stock ? "text-[#7BC47F] border-[#7BC47F]/40" : "text-[#E05A36] border-[#E05A36]/40"}`} data-testid="product-stock-badge">
              {product.in_stock ? "In stock" : "Sold out"}
            </span>
          </div>
          <p className="text-sm sm:text-base text-[#B8AEA5] leading-relaxed mb-8" data-testid="product-description">
            {product.description}
          </p>

          {product.colors && product.colors.length > 0 && (
            <div className="mb-8">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#7A7067] mb-3">
                Select color — {product.colors.length} options
              </p>
              <div className="relative" data-testid="color-selector">
                <button
                  onClick={() => setColorOpen((o) => !o)}
                  className="w-full sm:w-80 flex items-center justify-between gap-3 border border-[#2C2623] hover:border-[#E05A36] px-4 py-3 text-sm font-mono transition-colors"
                  data-testid="color-dropdown-button"
                >
                  <span className="flex items-center gap-3">
                    <span
                      className="w-4 h-4 rounded-full border border-[#2C2623] shrink-0"
                      style={{ backgroundColor: COLOR_HEX[(color || product.colors[0]).toLowerCase()] || "#7A7067" }}
                    />
                    {color || product.colors[0]}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${colorOpen ? "rotate-180" : ""}`} />
                </button>
                {colorOpen && (
                  <div className="absolute z-20 mt-1 w-full sm:w-80 max-h-64 overflow-y-auto bg-[#171412] border border-[#2C2623] shadow-[0_20px_40px_rgba(0,0,0,0.5)]" data-testid="color-dropdown-list">
                    {product.colors.map((c) => (
                      <button
                        key={c}
                        onClick={() => selectColor(c)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-mono text-left transition-colors ${
                          color === c ? "bg-[#E05A36] text-[#0C0A09] font-bold" : "hover:bg-[#1E1A17]"
                        }`}
                        data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-[#2C2623] shrink-0"
                          style={{ backgroundColor: COLOR_HEX[c.toLowerCase()] || "#7A7067" }}
                        />
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#7A7067] mb-3">Select size</p>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="size-selector">
            {product.sizes.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={`min-w-[3rem] px-3 py-2.5 text-sm font-mono border transition-colors ${
                  size === s
                    ? "bg-[#E05A36] text-[#0C0A09] border-[#E05A36] font-bold"
                    : "border-[#2C2623] hover:border-[#E05A36] hover:text-[#E05A36]"
                }`}
                data-testid={`size-option-${s}`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-8">
            <div className="flex items-center border border-[#2C2623]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:text-[#E05A36]" data-testid="qty-minus-button">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-4 font-mono" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:text-[#E05A36]" data-testid="qty-plus-button">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <button
            disabled={!product.in_stock}
            onClick={() => {
              if (!size) {
                setSize(product.sizes[0]);
              }
              addItem(product, size || product.sizes[0], qty, color || product.colors?.[0] || null);
              setDrawerOpen(true);
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider px-10 py-4 hover:bg-[#F06B47] hover:shadow-[0_0_25px_rgba(224,90,54,0.4)] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-to-cart-button"
          >
            <ShoppingBag className="w-4 h-4" /> Add to cart — ${(product.price * qty).toFixed(2)}
          </button>
          <p className="text-[11px] font-mono text-[#7A7067] uppercase tracking-widest mt-4">
            Free shipping over $150 • Code REBEL15 saves you 15%
          </p>
        </Reveal>
      </div>
    </div>
  );
};

export default ProductDetail;
